// 轻量、类型安全的 API 客户端。
// 不引第三方 HTTP 库；fetch + OpenAPI 生成的类型即够。
// 认证：若有 token（localStorage），自动注入 Authorization: Bearer。
import type {
  SceneType,
  SceneMeta,
  SceneData,
  Widget,
  ExportPreset,
  RenderRequest,
  RenderResult,
  AudioEffect,
  Project,
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  CreateProjectRequest,
  UpdateProjectRequest,
} from './types'

const BASE = '/api/v1'
const TOKEN_KEY = 'vw_token'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

/** 取本地 token（若有），无则空对象（游客请求）。 */
function authHeader(): Record<string, string> {
  if (typeof localStorage === 'undefined') return {}
  const tk = localStorage.getItem(TOKEN_KEY)
  return tk ? { Authorization: `Bearer ${tk}` } : {}
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = (await res.json()) as { error?: string }
      if (j?.error) msg = j.error
    } catch {
      /* 非 JSON 错误体，保留默认 msg */
    }
    throw new ApiError(res.status, msg)
  }
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) return undefined as T
  return (await res.json()) as T
}

function post<T>(path: string, body: unknown) {
  return http<T>(path, { method: 'POST', body: JSON.stringify(body) })
}

/**
 * autofillStream — AI 填充的 SSE 流式消费。
 * 后端 text/event-stream：data:{delta}（多次）→ event:done data:{data} / event:error。
 * onDelta 收到每段增量（供进度展示）；resolve 最终 data；失败抛 ApiError。
 */
async function autofillStream(
  id: SceneType,
  hint: string,
  onDelta?: (delta: string, totalChars: number) => void,
  signal?: AbortSignal,
): Promise<{ data: SceneData }> {
  const res = await fetch(BASE + `/scenes/${id}/autofill`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeader() },
    body: JSON.stringify({ hint }),
    signal,
  })
  if (!res.ok || !res.body) {
    let msg = `HTTP ${res.status}`
    try {
      const j = (await res.json()) as { error?: string }
      if (j?.error) msg = j.error
    } catch {
      /* 非 JSON 错误体 */
    }
    throw new ApiError(res.status, msg)
  }

  const reader = res.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  let total = 0
  let result: SceneData | undefined
  let evtError: string | undefined

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buf += dec.decode(value, { stream: true })
    let sep: number
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, sep)
      buf = buf.slice(sep + 2)
      let event = ''
      let dataLine = ''
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) dataLine += line.slice(5).trim()
      }
      if (!dataLine) continue
      let payload: any
      try {
        payload = JSON.parse(dataLine)
      } catch {
        continue
      }
      if (event === 'done') {
        result = payload.data as SceneData
      } else if (event === 'error') {
        evtError = payload.error as string
      } else if (payload.delta) {
        total += (payload.delta as string).length
        onDelta?.(payload.delta as string, total)
      }
    }
  }

  if (evtError) throw new ApiError(500, evtError)
  if (!result) throw new ApiError(500, 'AI 返回为空')
  return { data: result }
}

export const api = {
  // 场景
  listScenes: () => http<{ scenes: SceneMeta[]; total: number }>('/scenes'),
  getScene: (id: SceneType) =>
    http<{ meta: SceneMeta; default_data: SceneData }>(`/scenes/${id}`),
  sceneWidgets: (id: SceneType, data: SceneData) =>
    post<{ widgets: Widget[] }>(`/scenes/${id}/widgets`, { data }),

  // AI 自动填充场景数据（SSE 流式；需后端配 AI key）
  autofillStream,

  // 用户反馈（转发 Telegram）
  submitFeedback: (message: string, contact: string) =>
    post<{ message: string }>('/feedback', { message, contact }),

  // 导出
  getExportPresets: () =>
    http<{ presets: Record<string, ExportPreset> }>('/exports/presets'),
  getExportConfig: () =>
    http<{ force_browser_export: boolean; watermark: { text: string; position: string; opacity: number; enabled: boolean } }>('/exports/config'),
  renderExport: (req: RenderRequest) =>
    post<RenderResult>('/exports/render', req),
  /** 异步导出：入队，立即返回 task_id（客户端轮询 getExportStatus） */
  enqueueExport: (req: RenderRequest) =>
    post<{ task_id: string; status: string }>('/exports', req),
  /** 查询异步导出状态（pending/rendering/done/failed + progress/output_url） */
  getExportStatus: (id: string) =>
    http<{ status: string; progress?: number; output_url?: string; file_size?: number; error?: string }>(
      `/exports/${id}`,
    ),
  /** 触发浏览器下载已渲染的 MP4（相对路径，经代理/同源命中 Go） */
  exportDownloadUrl: (id: string) => `${BASE}/exports/${id}/download`,

  // 音效
  listAudioEffects: () => http<{ effects: AudioEffect[] }>('/audio/effects'),

  // 认证（注册不返 token，需登录；登录返 access_token + user）
  register: (body: RegisterRequest) => post<{ message: string }>('/auth/register', body),
  /** 发送邮箱验证码（注册前验证邮箱有效） */
  sendCode: (email: string) => post<{ message: string }>('/auth/send-code', { email }),
  login: (body: LoginRequest) => post<AuthResponse>('/auth/login', body),
  /** Google OAuth 配置（决定前端是否显示 Google 登录按钮） */
  authConfig: () => http<{ google_client_id: string; enabled: boolean; skip_auth: boolean; enable_email_login: boolean }>('/auth/config'),
  /** Google 登录：用 GIS 回调拿到的 ID credential 换自家 JWT */
  googleLogin: (credential: string) => post<AuthResponse>('/auth/google', { credential }),

  // 项目（需登录：token 自动注入；后端按 user_id 归属）
  listProjects: () => http<{ projects: Project[] }>('/projects'),
  createProject: (body: CreateProjectRequest) =>
    post<{ project: Project }>('/projects', body),
  getProject: (id: string) => http<{ project: Project }>(`/projects/${id}`),
  updateProject: (id: string, body: UpdateProjectRequest) =>
    http<{ status: string }>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteProject: (id: string) =>
    http<{ status: string }>(`/projects/${id}`, { method: 'DELETE' }),
}
